import api from './api';

/**
 * authService — handles login, logout, and registration, and is responsible
 * for stashing the JWT, user info, and role/site assignments in localStorage
 * after a successful login. The axios interceptor in api.js then attaches
 * the JWT and active-context headers to all subsequent requests automatically.
 *
 * Assumption about login flow:
 *   `dbName` is already in localStorage before login() is called — it's set
 *   during the domain-check step on the Login page. Without it the backend
 *   returns 400 (no x-db-name header).
 *
 *   On a successful login the server returns:
 *     {
 *       userFound: true,
 *       uid, uname,
 *       jwt,                       // signed token, 1 week expiry
 *       assignments: [...],        // user's role+site assignments
 *       defaultAssignment: {...}   // the one flagged is_default_context = true,
 *                                  //   or most recent if none flagged
 *     }
 *
 *   We store the JWT, the user identity, the full assignments list, and we
 *   seed roleUrid + siteCode from defaultAssignment so the first authenticated
 *   request from any page has the correct context headers.
 */

const STORAGE_KEYS = [
  'jwt',
  'uid',
  'uname',
  'assignments',
  'roleUrid',
  'siteCode',
  'budgetVersionId',
];

const clearSession = () => {
  STORAGE_KEYS.forEach((k) => localStorage.removeItem(k));
  // Note: dbName is intentionally NOT cleared — it's needed for re-login
  // and the domain-check flow runs before login on first visit.
};

const authService = {
  login: async (username, password) => {
    const response = await api.post('/users/login', {
      uname: username,
      upasswd: password,
    });

    const data = response.data || {};

    // Server returns userFound: false on bad creds — surface that to caller
    if (!data.userFound || !data.jwt) {
      return response;
    }

    localStorage.setItem('jwt', data.jwt);
    localStorage.setItem('uid', String(data.uid));
    localStorage.setItem('uname', data.uname);

    if (Array.isArray(data.assignments) && data.assignments.length > 0) {
      localStorage.setItem('assignments', JSON.stringify(data.assignments));
    }

    if (data.defaultAssignment) {
      const da = data.defaultAssignment;
      localStorage.setItem('roleUrid', String(da.urid));
      if (da.site_code) {
        localStorage.setItem('siteCode', da.site_code);
      } else {
        // "All sites" assignment — clear any stale site_code
        localStorage.removeItem('siteCode');
      }
    }

    return response;
  },

  logout: async () => {
    try {
      // Best-effort server notification; clear local session either way
      await api.get('/users/logout');
    } catch (err) {
      // ignore — we're logging out regardless
    }
    clearSession();
  },

  register: async (email, username, password, confirmPassword) => {
    return api.post('/users/register', {
      email,
      uname: username,
      passwd: password,
      confPasswd: confirmPassword,
    });
  },

  isAuthenticated: () => !!localStorage.getItem('jwt'),

  getCurrentUid: () => {
    const raw = localStorage.getItem('uid');
    return raw ? parseInt(raw, 10) : null;
  },

  getCurrentUname: () => localStorage.getItem('uname') || '',

  clearSession,
};

export default authService;